/**
 * @file ModifyButton.jsx
 * @description A button component for triggering modification actions on elements, designed to be reusable and customizable.
 * @requires React, PropTypes, react-i18next
 * @exports ModifyButton
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ActionButton from './ActionButton';

/**
 * @typedef {Object} ModifyButtonProps
 * @property {Function} action - Callback function to handle the modification action. Should accept an `elementId`.
 * @property {string} elementId - Identifier of the element to be modified.
 * @property {string} [label='Modify this element'] - Text label displayed on the button.
 * @property {string} [icon='pencil'] - Icon displayed on the button.
 * @property {string} [tooltipText] - Tooltip text displayed on hover. Defaults to localized "Edit" text.
 * @property {string} [color='blue'] - Color theme of the button.
 */

/**
 * ModifyButton is a reusable component wrapping the ActionButton, specialized for modification tasks.
 *
 * @component
 * @param {ModifyButtonProps} props - Component properties.
 * @returns {JSX.Element} A styled button component with an action and tooltip for modifying elements.
 * @throws {Error} If required props `action` or `elementId` are missing.
 *
 * @example
 * const handleEdit = (id) => console.log(`Editing element with ID: ${id}`);
 * 
 * // Basic usage
 * <ModifyButton
 *   action={handleEdit}
 *   elementId="12345"
 * />
 *
 * // Customized button
 * <ModifyButton
 *    action={handleEdit}
 *    elementId="12345"
 *    label="Edit"
 *    icon="edit"
 *    tooltipText="Click to edit"
 *    color="secondary"
 * />
 */
const ModifyButton = ({ 
    action, 
    elementId, 
    label = 'Modify this element', 
    icon = 'pencil', 
    tooltipText, 
    color = 'blue' 
}) => {
    if (!action) {
        throw new Error("ModifyButton: Missing required prop 'action'. Expected a function.");
    }

    if (!elementId) {
        throw new Error("ModifyButton: Missing required prop 'elementId'. Expected a string identifier.");
    }

    const { t } = useTranslation();

    const computedTooltipText = useMemo(() => tooltipText || t('Popup_Edit'), [tooltipText, t]);
    
    return (
        <ActionButton
            action={action}
            elementId={elementId}
            label={label}
            icon={icon}
            tooltipText={computedTooltipText}
            color={color}
        />
    );
};

ModifyButton.propTypes = {
    action: PropTypes.func.isRequired,
    elementId: PropTypes.string.isRequired,
    label: PropTypes.string,
    icon: PropTypes.string,
    tooltipText: PropTypes.string,
    color: PropTypes.string
};

export default ModifyButton;
